/**
 * The page's half of the Android bridge. The contract is
 * docs/android-bridge.md; the phone's half is assist-mobile's `web/Bridge.kt`
 * and `PageBridge.kt`.
 *
 * `window.assistNative` exists from document start inside the Android app,
 * on Assist's own origin only. Everywhere else it is absent and every call
 * here answers null, so the page behaves as it does on the web.
 */

/** Inside the Android app, whether or not the bridge is there. */
export const inAndroid = (): boolean =>
  typeof navigator !== 'undefined' && navigator.userAgent.includes('MauvelyAssistAndroid/');

interface NativeChannel {
  postMessage(message: string): void;
  onmessage: ((event: MessageEvent<string>) => void) | null;
}

declare global {
  interface Window {
    assistNative?: NativeChannel;
  }
}

/** What was captured of the screen the assistant was opened over. */
export interface ScreenOffer {
  text: boolean;
  image: boolean;
}

export interface Hello {
  surface: 'app' | 'assistant';
  assistantRole: boolean;
  screen: ScreenOffer | null;
}

export interface ScreenCapture {
  app: string | null;
  text: string | null;
  jpegBase64: string | null;
}

type Reply = { id: number; ok: boolean; error?: string } & Record<string, unknown>;

/** How long a reply is waited for; the phone answers in milliseconds. */
const WAIT_MS = 10_000;

let nextId = 1;
const waiting = new Map<number, (reply: Reply | null) => void>();
const screenListeners = new Set<(screen: ScreenOffer | null) => void>();
const quoteListeners = new Set<(text: string) => void>();
const pairingListeners = new Set<(pcs: string[]) => void>();
let listening = false;

/** The PC ids in an answer or event; null for anything that is not a list. */
const pcList = (v: unknown): string[] | null => (Array.isArray(v) ? v.filter((id): id is string => typeof id === 'string') : null);

function channel(): NativeChannel | null {
  const c = typeof window !== 'undefined' ? window.assistNative : undefined;
  if (!c) return null;
  if (!listening) {
    listening = true;
    c.onmessage = (e) => {
      let m: Reply & { event?: string; screen?: ScreenOffer | null; text?: unknown; pcs?: unknown };
      try {
        m = JSON.parse(e.data) as typeof m;
      } catch {
        return;
      }
      if (typeof m.id === 'number') {
        waiting.get(m.id)?.(m);
        waiting.delete(m.id);
      } else if (m.event === 'screen') {
        for (const l of screenListeners) l(m.screen ?? null);
      } else if (m.event === 'quote' && typeof m.text === 'string') {
        for (const l of quoteListeners) l(m.text);
      } else if (m.event === 'pairings') {
        const pcs = pcList(m.pcs);
        if (pcs) for (const l of pairingListeners) l(pcs);
      }
    };
  }
  return c;
}

function ask(message: Record<string, unknown>): Promise<Reply | null> {
  const c = channel();
  if (!c) return Promise.resolve(null);
  const id = nextId++;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiting.delete(id);
      resolve(null);
    }, WAIT_MS);
    waiting.set(id, (reply) => {
      clearTimeout(timer);
      resolve(reply);
    });
    c.postMessage(JSON.stringify({ ...message, id }));
  });
}

export async function hello(): Promise<Hello | null> {
  const r = await ask({ type: 'hello' });
  return r?.ok ? { surface: r.surface as Hello['surface'], assistantRole: !!r.assistantRole, screen: (r.screen as ScreenOffer | null) ?? null } : null;
}

export async function screenCapture(): Promise<ScreenCapture | null> {
  const r = await ask({ type: 'screen' });
  return r?.ok ? { app: (r.app as string | null) ?? null, text: (r.text as string | null) ?? null, jpegBase64: (r.jpegBase64 as string | null) ?? null } : null;
}

let announced = false;

/** The first screen is drawn: `hello`, once a page load. On the app it lifts
 *  the phone's own loading screen (docs/android-bridge.md). */
export function announce(): void {
  if (announced || !hasBridge()) return;
  announced = true;
  void hello();
}

/** One image shared to the app, as the phone hands it over. */
interface SharedEntry {
  name: string;
  mime: string;
  base64: string;
}

/** Images shared to the app from another one, taken once. Empty when there
 *  are none, or the app is too old to hand them over. */
export async function sharedImages(): Promise<File[]> {
  const r = await ask({ type: 'shared' });
  if (!r?.ok || !Array.isArray(r.files)) return [];
  const files: File[] = [];
  for (const entry of r.files as SharedEntry[]) {
    if (typeof entry?.name !== 'string' || typeof entry.mime !== 'string' || !entry.mime.startsWith('image/')) continue;
    if (typeof entry.base64 !== 'string') continue;
    let binary: string;
    try {
      binary = atob(entry.base64);
    } catch {
      continue;
    }
    files.push(new File([Uint8Array.from(binary, (c) => c.charCodeAt(0))], entry.name, { type: entry.mime }));
  }
  return files;
}

/** Closes the assistant sheet. The reply may never come: the page goes too. */
export const closeSheet = (): void => void ask({ type: 'close' });

/** Opens the full app on `path` and closes the sheet. */
export const openInApp = async (path: string): Promise<boolean> => !!(await ask({ type: 'open', path }))?.ok;

/** Android's settings, where the digital assistant is chosen. */
export const assistantSettings = async (): Promise<boolean> => !!(await ask({ type: 'assistantSettings' }))?.ok;

/** The bridge is there: the page is in the Android app, on its own origin. */
export const hasBridge = (): boolean => typeof window !== 'undefined' && !!window.assistNative;

/** What the selection would quote, or null: the app offers Quote in its
 *  selection bar while there is something. False when the app has no Quote
 *  to offer (an older app), and the page draws its own. */
export const offerQuote = async (text: string | null): Promise<boolean> =>
  !!(await ask({ type: 'quotable', text }))?.ok;

/** Hears Quote tapped in the selection bar, with the words last offered. */
export function onQuoteTap(fn: (text: string) => void): () => void {
  channel();
  quoteListeners.add(fn);
  return () => quoteListeners.delete(fn);
}

/** The PCs this phone holds a key for, and so may approve for (docs/dispatch.md,
 *  "Approving from a paired phone"). Null where the app cannot pair: an
 *  older one, or none. */
export async function nativePairings(): Promise<string[] | null> {
  const r = await ask({ type: 'pairings' });
  return r?.ok ? pcList(r.pcs) : null;
}

/** Opens the phone's scanner, for a PC's pairing code. */
export const nativePair = async (): Promise<boolean> => !!(await ask({ type: 'pair' }))?.ok;

/** Opens the phone's own sheet for the reply `messageId` in `chatId`, the
 *  thread of the PC `deviceId`. False where it did not open: this phone is
 *  not paired with that PC, or an id was refused. */
export const nativeApprove = async (deviceId: string, chatId: string, messageId: string): Promise<boolean> =>
  !!(await ask({ type: 'approve', deviceId, chatId, messageId }))?.ok;

/** Hears when a pairing is kept or removed, with the PCs it leaves. */
export function onPairings(fn: (pcs: string[]) => void): () => void {
  channel();
  pairingListeners.add(fn);
  return () => pairingListeners.delete(fn);
}

/** Hears when the capture changes; it often arrives after `hello`. */
export function onScreen(fn: (screen: ScreenOffer | null) => void): () => void {
  screenListeners.add(fn);
  return () => screenListeners.delete(fn);
}

/** The capture as the files a message takes: a picture and the words. */
export function captureFiles(capture: ScreenCapture): File[] {
  const files: File[] = [];
  if (capture.jpegBase64) {
    const bytes = Uint8Array.from(atob(capture.jpegBase64), (c) => c.charCodeAt(0));
    files.push(new File([bytes], 'Screen.jpg', { type: 'image/jpeg' }));
  }
  if (capture.text) {
    const from = capture.app ? `From ${capture.app}:\n\n` : '';
    files.push(new File([from + capture.text], 'Screen text.txt', { type: 'text/plain' }));
  }
  return files;
}
